'use client';

import { CaretSortIcon, CheckIcon } from '@radix-ui/react-icons';
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';

import { cn } from '../utils';
import { type ScrollBar } from './scrollArea';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) => (
  <SelectPrimitive.Trigger
    className={cn(
      'border-input ring-offset-background placeholder:text-muted-foreground data-[placeholder]:text-muted-foreground focus:ring-ring touch:h-11 touch:text-base flex h-9 w-full items-center justify-between gap-2 truncate rounded-md border bg-transparent px-3 py-0 text-sm shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="min-w-0 truncate">{children}</span>
    <SelectPrimitive.Icon asChild>
      <CaretSortIcon className="size-4 shrink-0 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const scrollAreaThumbClassName = 'relative flex-1 rounded-full bg-primary';
const getScrollAreaScrollbarClassName = ({
  className,
  orientation = 'vertical',
}: Pick<React.ComponentProps<typeof ScrollBar>, 'className' | 'orientation'> = {}) => {
  return cn(
    'flex touch-none select-none transition-colors',
    orientation === 'vertical' && 'h-full w-2.5 p-0.5',
    orientation === 'horizontal' && 'h-2.5 flex-col p-0.5',
    className,
  );
};

const SelectContent = ({
  className,
  viewportClassName,
  children,
  position = 'popper',
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  /** Extra classes for the scrolling list, e.g. a taller max height than the 256px popper default. */
  viewportClassName?: string;
}) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-[8rem] overflow-hidden rounded-md border shadow-md',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.stopPropagation();
        }
      }}
      {...props}
    >
      <ScrollAreaPrimitive.Root type="auto">
        <SelectPrimitive.Viewport
          asChild
          // https://github.com/radix-ui/primitives/issues/2059#issuecomment-1492071891
          style={{ overflowY: undefined }}
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] max-h-[256px]  w-full min-w-[var(--radix-select-trigger-width)]',
            viewportClassName,
          )}
        >
          <ScrollAreaPrimitive.Viewport className={'h-full w-full rounded-[inherit]'}>
            {children}
          </ScrollAreaPrimitive.Viewport>
        </SelectPrimitive.Viewport>
        <ScrollAreaPrimitive.Scrollbar
          className={getScrollAreaScrollbarClassName({ className: 'w-2.5 py-1 pr-1' })}
          orientation="vertical"
        >
          <ScrollAreaPrimitive.Thumb className={cn('w-1', scrollAreaThumbClassName)} />
        </ScrollAreaPrimitive.Scrollbar>
      </ScrollAreaPrimitive.Root>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) => (
  <SelectPrimitive.Label
    className={cn('px-2 py-1.5 text-sm font-semibold', className)}
    {...props}
  />
);
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) => (
  <SelectPrimitive.Item
    className={cn(
      'focus:bg-accent focus:text-accent-foreground relative flex w-full max-w-[80vw] cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
);
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) => (
  <SelectPrimitive.Separator className={cn('bg-muted -mx-1 my-1 h-px', className)} {...props} />
);
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const SimpleSelect = <
  const T extends
    | string
    | {
        value: string;
        label: React.ReactNode;
      },
>({
  selectedValue,
  values,
  onChange,
  placeholder,
  disabled = false,
  triggerClassName,
  contentClassName,
  id,
}: {
  onChange: (newValue: T extends object ? T['value'] : T) => void;
  values: T[] | readonly T[];
  selectedValue?: string;
  placeholder?: string;
  disabled?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  id?: string;
}) => {
  return (
    <Select
      disabled={disabled}
      value={selectedValue}
      onValueChange={(value) => {
        onChange(value as T extends object ? T['value'] : T);
      }}
    >
      <SelectTrigger className={triggerClassName} id={id} tabIndex={0}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        className={cn(
          'w-[var(--radix-select-trigger-width)] max-w-full overflow-y-auto',
          contentClassName,
        )}
      >
        <SelectGroup>
          {values.map((item) => {
            const _value = typeof item === 'string' ? item : item.value;
            const label = typeof item === 'string' ? item : item.label;
            return (
              <SelectItem value={_value} key={_value}>
                {label}
              </SelectItem>
            );
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SimpleSelect,
};
